// parseMapLinkCoordinates — explicit map-URL → coordinates parser.
//
// Ownership: frontend owns direct coordinate URL parsing for create/edit UX.
// Backend reimplements the same format matrix for short/opaque final URLs.
// Keep patterns aligned with apps.geo.services.map_link_coordinates.
//
// Returns:
//   { ok: true, provider, latitude, longitude }
//   { ok: false, needsResolve: true, provider, url }
//   { ok: false, code, message }
//
// Google semantic tiers (highest first), mirrored with backend:
//   1. place_target  — selected place marker !3dLAT!4dLON
//   2. explicit_query — coordinate-only q / query / ll
//   3. viewport       — camera center /@LAT,LON and center=LAT,LON

const PROVIDER_GOOGLE = "google";
const PROVIDER_APPLE = "apple";
const PROVIDER_OSM = "openstreetmap";

const GOOGLE_DIRECT_HOSTS = new Set([
  "google.com",
  "www.google.com",
  "maps.google.com",
  "google.com.ua",
  "www.google.com.ua",
  "maps.google.com.ua",
]);

const GOOGLE_SHORT_HOSTS = new Set(["maps.app.goo.gl", "goo.gl"]);
const APPLE_DIRECT_HOSTS = new Set(["maps.apple.com"]);
const APPLE_SHORT_HOSTS = new Set(["maps.apple"]);
const OSM_HOSTS = new Set(["openstreetmap.org", "www.openstreetmap.org"]);

const GOOGLE_TIER_PLACE_TARGET = "place_target";
const GOOGLE_TIER_EXPLICIT_QUERY = "explicit_query";
const GOOGLE_TIER_VIEWPORT = "viewport";
const GOOGLE_TIER_ORDER = [
  GOOGLE_TIER_PLACE_TARGET,
  GOOGLE_TIER_EXPLICIT_QUERY,
  GOOGLE_TIER_VIEWPORT,
];

const COORD_NUM = String.raw`[+-]?(?:\d+\.\d+|\d+\.|\.\d+|\d+)`;
const COORD_PAIR_RE = new RegExp(`^(${COORD_NUM})\\s*,\\s*(${COORD_NUM})$`);
const AT_COORDS_RE = new RegExp(
  `@(${COORD_NUM}),(${COORD_NUM})(?:,\\d+(?:\\.\\d+)?z)?`,
  "i",
);
const BANG_3D_4D_RE = new RegExp(`!3d(${COORD_NUM})!4d(${COORD_NUM})`, "gi");
const OSM_HASH_MAP_RE = new RegExp(
  `(?:^|[&#])map=\\d+(?:\\.\\d+)?/(${COORD_NUM})/(${COORD_NUM})(?:[&#]|$)`,
  "i",
);
const APPLE_SHARE_PATH_RE = /^\/p\/[A-Za-z0-9_-]+$/;

const MESSAGES = {
  empty: "Вставте посилання на карту",
  invalid_url: "Некоректне посилання на карту",
  unsupported: "Непідтримуване посилання на карту",
  no_coords: "У посиланні не знайдено однозначних координат",
  conflict: "У посиланні знайдено конфліктні координати",
  out_of_range: "Координати поза допустимими межами",
};

function formatCoordinate(value) {
  return String(Number(Number(value).toFixed(6)));
}

function parseFiniteDecimal(raw) {
  if (raw === null || raw === undefined) return null;
  const text = String(raw).trim();
  if (!text || /[eE]/.test(text)) return null;
  if (!new RegExp(`^${COORD_NUM}$`).test(text)) return null;
  const value = Number(text);
  if (!Number.isFinite(value)) return null;
  return value;
}

function validatePair(latitude, longitude) {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return { ok: false, code: "invalid", message: MESSAGES.no_coords };
  }
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    return { ok: false, code: "out_of_range", message: MESSAGES.out_of_range };
  }
  return {
    ok: true,
    latitude: formatCoordinate(latitude),
    longitude: formatCoordinate(longitude),
  };
}

function parseCoordinatePairText(raw) {
  if (raw === null || raw === undefined) return null;
  const text = String(raw).trim().replace(/\+/g, " ");
  const match = text.match(COORD_PAIR_RE);
  if (!match) return null;
  const latitude = parseFiniteDecimal(match[1]);
  const longitude = parseFiniteDecimal(match[2]);
  if (latitude === null || longitude === null) return null;
  return { latitude, longitude };
}

function pairKey(pair) {
  return `${formatCoordinate(pair.latitude)}:${formatCoordinate(pair.longitude)}`;
}

function resolveUniquePair(candidates) {
  if (!candidates.length) {
    return { ok: false, code: "no_coords", message: MESSAGES.no_coords };
  }
  const unique = new Map();
  for (const candidate of candidates) {
    unique.set(pairKey(candidate), candidate);
  }
  if (unique.size > 1) {
    return { ok: false, code: "conflict", message: MESSAGES.conflict };
  }
  const only = unique.values().next().value;
  return validatePair(only.latitude, only.longitude);
}

function hostOf(parsed) {
  return String(parsed.hostname || "").toLowerCase();
}

function isDefaultHttpsPort(parsed) {
  return !parsed.port || parsed.port === "443";
}

function rejectCredentials(parsed) {
  return Boolean(parsed.username || parsed.password);
}

function isIpLiteralHost(hostname) {
  if (!hostname) return true;
  if (hostname.startsWith("[") && hostname.endsWith("]")) return true;
  if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname)) return true;
  return false;
}

function isAppleSharePath(pathname) {
  return APPLE_SHARE_PATH_RE.test(pathname || "");
}

function collectGoogleTieredPairs(parsed) {
  const tiers = {
    [GOOGLE_TIER_PLACE_TARGET]: [],
    [GOOGLE_TIER_EXPLICIT_QUERY]: [],
    [GOOGLE_TIER_VIEWPORT]: [],
  };
  const pathAndSearch = `${parsed.pathname || ""}${parsed.search || ""}`;

  BANG_3D_4D_RE.lastIndex = 0;
  let bangMatch = BANG_3D_4D_RE.exec(pathAndSearch);
  while (bangMatch) {
    const latitude = parseFiniteDecimal(bangMatch[1]);
    const longitude = parseFiniteDecimal(bangMatch[2]);
    if (latitude !== null && longitude !== null) {
      tiers[GOOGLE_TIER_PLACE_TARGET].push({ latitude, longitude });
    }
    bangMatch = BANG_3D_4D_RE.exec(pathAndSearch);
  }

  const params = parsed.searchParams;
  for (const key of ["q", "query", "ll"]) {
    for (const value of params.getAll(key)) {
      const pair = parseCoordinatePairText(value);
      if (pair) {
        tiers[GOOGLE_TIER_EXPLICIT_QUERY].push(pair);
      }
    }
  }

  const atMatch = pathAndSearch.match(AT_COORDS_RE);
  if (atMatch) {
    const latitude = parseFiniteDecimal(atMatch[1]);
    const longitude = parseFiniteDecimal(atMatch[2]);
    if (latitude !== null && longitude !== null) {
      tiers[GOOGLE_TIER_VIEWPORT].push({ latitude, longitude });
    }
  }
  for (const value of params.getAll("center")) {
    const pair = parseCoordinatePairText(value);
    if (pair) {
      tiers[GOOGLE_TIER_VIEWPORT].push(pair);
    }
  }

  return tiers;
}

function resolveGooglePairs(parsed) {
  const tiers = collectGoogleTieredPairs(parsed);
  for (const tierName of GOOGLE_TIER_ORDER) {
    const candidates = tiers[tierName];
    if (candidates.length) {
      return resolveUniquePair(candidates);
    }
  }
  return { ok: false, code: "no_coords", message: MESSAGES.no_coords };
}

function collectApplePairs(parsed) {
  // Apple `sll` is search/viewport context, not a property target coordinate.
  // Mirror backend parse_qs: evaluate every repeated query value.
  const candidates = [];
  const params = parsed.searchParams;
  for (const key of ["ll", "q"]) {
    for (const value of params.getAll(key)) {
      const pair = parseCoordinatePairText(value);
      if (pair) {
        candidates.push(pair);
      }
    }
  }
  return candidates;
}

function collectOsmPairs(parsed) {
  const candidates = [];
  const hash = String(parsed.hash || "").replace(/^#/, "");
  if (hash) {
    const hashMatch = hash.match(
      new RegExp(`(?:^|&)map=\\d+(?:\\.\\d+)?/(${COORD_NUM})/(${COORD_NUM})(?:&|$)`, "i"),
    );
    if (hashMatch) {
      const latitude = parseFiniteDecimal(hashMatch[1]);
      const longitude = parseFiniteDecimal(hashMatch[2]);
      if (latitude !== null && longitude !== null) {
        candidates.push({ latitude, longitude });
      }
    }
  }

  const combined = `${parsed.search || ""}#${hash}`;
  const mapMatch = combined.match(OSM_HASH_MAP_RE);
  if (mapMatch) {
    const latitude = parseFiniteDecimal(mapMatch[1]);
    const longitude = parseFiniteDecimal(mapMatch[2]);
    if (latitude !== null && longitude !== null) {
      candidates.push({ latitude, longitude });
    }
  }

  const params = parsed.searchParams;
  if (params.has("mlat") && params.has("mlon")) {
    const latitude = parseFiniteDecimal(params.get("mlat"));
    const longitude = parseFiniteDecimal(params.get("mlon"));
    if (latitude !== null && longitude !== null) {
      candidates.push({ latitude, longitude });
    }
  }

  return candidates;
}

function classifyHost(hostname, pathname) {
  if (GOOGLE_SHORT_HOSTS.has(hostname)) {
    return { provider: PROVIDER_GOOGLE, short: true };
  }
  if (GOOGLE_DIRECT_HOSTS.has(hostname)) {
    return { provider: PROVIDER_GOOGLE, short: false };
  }
  if (APPLE_SHORT_HOSTS.has(hostname)) {
    if (isAppleSharePath(pathname)) {
      return { provider: PROVIDER_APPLE, short: true };
    }
    return null;
  }
  if (APPLE_DIRECT_HOSTS.has(hostname)) {
    return { provider: PROVIDER_APPLE, short: false };
  }
  if (OSM_HOSTS.has(hostname)) {
    return { provider: PROVIDER_OSM, short: false };
  }
  return null;
}

/**
 * Parse a pasted map URL into validated coordinates, or signal short-link resolve.
 * @param {string} rawUrl
 */
export function parseMapLinkCoordinates(rawUrl) {
  const trimmed = String(rawUrl ?? "").trim();
  if (!trimmed) {
    return { ok: false, code: "empty", message: MESSAGES.empty };
  }

  let parsed;
  try {
    parsed = new URL(trimmed);
  } catch {
    return { ok: false, code: "invalid_url", message: MESSAGES.invalid_url };
  }

  if (parsed.protocol !== "https:") {
    return { ok: false, code: "unsupported", message: MESSAGES.unsupported };
  }
  if (rejectCredentials(parsed) || !isDefaultHttpsPort(parsed)) {
    return { ok: false, code: "unsupported", message: MESSAGES.unsupported };
  }

  const hostname = hostOf(parsed);
  if (!hostname || isIpLiteralHost(hostname)) {
    return { ok: false, code: "unsupported", message: MESSAGES.unsupported };
  }

  const classified = classifyHost(hostname, parsed.pathname || "/");
  if (!classified) {
    return { ok: false, code: "unsupported", message: MESSAGES.unsupported };
  }

  if (classified.short) {
    if (hostname === "goo.gl" && !parsed.pathname.toLowerCase().startsWith("/maps")) {
      return { ok: false, code: "unsupported", message: MESSAGES.unsupported };
    }
    return {
      ok: false,
      needsResolve: true,
      provider: classified.provider,
      url: trimmed,
    };
  }

  let resolved;
  if (classified.provider === PROVIDER_GOOGLE) {
    resolved = resolveGooglePairs(parsed);
  } else if (classified.provider === PROVIDER_APPLE) {
    resolved = resolveUniquePair(collectApplePairs(parsed));
  } else if (classified.provider === PROVIDER_OSM) {
    resolved = resolveUniquePair(collectOsmPairs(parsed));
  } else {
    return { ok: false, code: "unsupported", message: MESSAGES.unsupported };
  }

  if (!resolved.ok) {
    return resolved;
  }

  return {
    ok: true,
    provider: classified.provider,
    latitude: resolved.latitude,
    longitude: resolved.longitude,
  };
}

export const MAP_LINK_PARSE_MESSAGES = MESSAGES;
export const MAP_LINK_PROVIDERS = {
  GOOGLE: PROVIDER_GOOGLE,
  APPLE: PROVIDER_APPLE,
  OSM: PROVIDER_OSM,
};
